<script lang="ts">
	import { format } from 'svelte-i18n';
	import { Container } from '@Shelly/shelly-design-system';
	import { readDeviceSettings, readDeviceStatus } from 'src/stores/device-runtime/status-selectors';
import { devices } from 'src/stores/lists/devices';
import { rooms } from 'src/stores/lists/rooms';
	import { getSettings } from 'src/helpers/get_status_settings_helpers';
	import {
		extractDevicesFromAlarm,
		filterSensors,
		foundedAlarmDevicesAndIndex,
		getBthomeShellyModelType,
		sortRoomsWithMissingDevices,
	} from '../../../Lists/list-helpers';
	import { isMotion, isShellyPresence, real_id, shellyId16 } from 'src/helpers/interface_helpers';
	import { buildPresenceConditionName } from 'src/components/Scenes/SceneWizard/Process/When/AddEditContionWizard/Steps/DeviceBased/helpers/presence-helpers';
	import { capFirstChar, _deepClone, _deepCopyArr } from 'src/helpers/utilities';
	import SelectList from 'src/components/SelectList/SelectList.svelte';
	import type { Room } from 'src/components/SelectList/SelectList.types';
	import { defaultHiddenDevicesRoom } from 'src/helpers/constants';
	import { isTestUser } from 'src/helpers/user_helpers';
	import { getPeripheralDigitalInputDevices } from 'src/helpers/addons/addon_device_helpers';

	export let selectedSensor, allSensorsRefs, alarm_object;
	export let hasPresenceSelected = false;
	export let selectedPresenceSensors: any[] = [];

	let selectedItems = [];

	/**
	 * Resolve the sensor forwarded to SelectZonesStep.
	 * Prefers the presence device regardless of selection order so zone configuration
	 * is always shown for the correct device.
	 */
	$: {
		const presenceItem = selectedItems.find((item) => isShellyPresence($devices[item.id]?.type));
		selectedSensor = presenceItem
			? { ...presenceItem }
			: selectedItems?.length > 0
				? { ...selectedItems[0] }
				: {};
	}

	$: hasPresenceSelected = selectedItems.some((item) => isShellyPresence($devices[item.id]?.type));

	/** All selected presence devices (for per-sensor zone UI in SelectZonesStep). */
	$: selectedPresenceSensors = selectedItems
		.filter((item) => isShellyPresence($devices[item.id]?.type))
		.map((item) => ({ ...item, ...$devices[item.id] }));

	let sensors = [];

	let count = 0;
	let roomsFiltered: Room[];

	const getMissingDevices = (allDevices) => {
		let deletedDevices = [];

		allDevices.forEach((id) => {
			let decimal = id.split(':')[1];
			let device = decimal ? $devices[shellyId16(decimal)] : undefined;
			if (!device) {
				deletedDevices.push({
					id: shellyId16(decimal),
				});
			}
		});

		return deletedDevices;
	};

	const filterDevicesByRoomID: any = (devices: any) => {
		//only devices from user account in rooms passed here
		let filteredDevicesByRoom = {};
		devices.forEach((device: any, i) => {
			const roomID = device.room_id;

			const allRooms = {
				...$rooms,
				...(isTestUser() && {
					[defaultHiddenDevicesRoom.value as number]: {
						name: $format(defaultHiddenDevicesRoom.title, {
							default: defaultHiddenDevicesRoom.translationDefault,
						}),
						id: defaultHiddenDevicesRoom.value,
					},
				}),
			};

			const deviceStatus = readDeviceStatus(device.id);
			const relayStatus = device.relay_id ? readDeviceStatus(device.relay_id) : undefined;
			const deviceData = {
				name: `${device.name}${
					!deviceStatus?.online && !relayStatus?.online
						? `- ${capFirstChar($format('label_offline'))}`
						: ''
				}`,
				id: device.id,
				selected: selectedItems.find((el) => el?.id === device?.id) ? true : false,
				roomName: allRooms[roomID]?.name || 'Unknown room',
				roomID: roomID,
				image: device.image ? device.image : undefined,
				icon: device.icon ? device.icon : undefined,
				backgroundColor: device?.backgroundColor,
				ext_sen_id: device.ext_sen_id,
			};

			if (filteredDevicesByRoom[roomID]) {
				filteredDevicesByRoom[roomID] = {
					cards: [...filteredDevicesByRoom[roomID].cards, deviceData],
					roomName: allRooms[roomID].name || 'Unknown room',
					roomID: roomID,
				};
			} else {
				filteredDevicesByRoom[roomID] = {
					cards: [deviceData],
					roomName: allRooms[roomID]?.name || 'Unknown room',
					roomID: roomID,
				};
			}
		});

		if (devices.length) {
			const allDevices = extractDevicesFromAlarm(alarm_object);
			const missingDevices = getMissingDevices(allDevices);

			if (missingDevices?.length) {
				filteredDevicesByRoom['missing_devices'] = {
					cards: [
						...missingDevices.map((device) => {
							return {
								name: device.id,
								id: device.id,
								roomID: 'missing_devices',
								roomName: $format('missing_devices_label', {
									default: 'Missing Devices',
								}),
								selected: true,
								icon: 'text-warning fas fa-warning',
							};
						}),
					],
					roomName: $format('missing_devices_label', {
						default: 'Missing Devices',
					}),
					roomID: 'missing_devices',
				};
			}
		}

		return sortRoomsWithMissingDevices(filteredDevicesByRoom);
	};

	function findDevices(devices) {
		let findDevices = [];
		devices.map((el) => {
			if (el && !el.hasOwnProperty('indailyint')) {
				findDevices.push(el);
			}
		});

		return findDevices;
	}

	$: if (
		alarm_object?.if?.or?.length > 0 ||
		(alarm_object?.if?.and && alarm_object?.if?.and.length > 0)
	) {
		count++;
		let items = alarm_object?.if?.or?.length > 0 ? alarm_object?.if?.or : alarm_object?.if?.and;

		// Build into a local array first — only assign to selectedItems when the set
		// of device IDs actually changes so selectedSensor never oscillates through {}
		// (which would momentarily flip isPresenceDevice and spawn extra tick() calls).
		let nextItems = [];
		items.map((dev) => {
			let hexId;
			if (dev.or) {
				let find_devices = findDevices(dev.or);

				if (find_devices.length > 0) {
					find_devices.forEach((selectedDev) => {
						hexId = shellyId16(selectedDev?.eq[0]?.rr?.split(':')[1]);
						if ($devices[hexId]) {
							nextItems.push($devices[hexId]);
						} else {
							nextItems.push({
								id: hexId,
								deleted: true,
							});
						}
					});
				}
			} else if (dev.eq) {
				let hexId = shellyId16(dev?.eq[0]?.rr?.split(':')[1]);

				if ($devices[hexId]) {
					nextItems.push($devices[hexId]);
				} else {
					nextItems.push({
						id: hexId,
						deleted: true,
					});
				}
			}
		});

		nextItems = nextItems.filter((el) => el !== undefined);
		nextItems = [...new Map(nextItems.map((item) => [item.id, item])).values()];

		const prevIds = selectedItems.map((i) => i.id).join(',');
		const nextIds = nextItems.map((i) => i.id).join(',');
		if (prevIds !== nextIds) {
			selectedItems = nextItems;
			/**
			 * Prefetch device settings for any newly visible presence device so zone
			 * data is ready before the user navigates to the Zones step.
			 */
			nextItems.forEach((item) => {
				if (isShellyPresence($devices[item.id]?.type) && !readDeviceSettings(item.id)) {
					getSettings($devices[item.id]);
				}
			});
		}
	} else {
		if (selectedItems.length > 0) selectedItems = [];
	}

	/**
	 * Referencing selectedItems explicitly ensures Svelte tracks it as a reactive
	 * dependency, so card checkbox states stay in sync when selectedItems is rebuilt
	 * from alarm_object rather than updated through a direct user interaction.
	 */
	$: roomsFiltered = (selectedItems, filterDevicesByRoomID(sensors));

	const findPropByType = (dev_id) => {
		let device = $devices[dev_id];
		if (!device) return;
		// Global presence zone: presencezone:200 → prsn_st4201 (zone_id + 1)
		if (isShellyPresence(device.type)) {
			const prop = buildPresenceConditionName('st', '200');
			return prop;
		}
		const modelType = getBthomeShellyModelType(device) || device.type;
		if (['SBDW-002D', 'SBDW-002C', 'SBDW-103C', 'SBDW-203C', 'SBDW-302C'].includes(modelType))
			return 'window';
		if (['SHDW-1', 'SHDW-2'].includes(modelType)) return 'door';
		if (isMotion(modelType)) return 'motion';
		const addonDevice = getPeripheralDigitalInputDevices(device)[0];
		if (addonDevice) return addonDevice.ext_sen_id;
		return 'pir';
	};

	const findCompValueByType = (dev_id) => {
		const device = $devices[dev_id];
		// prsn_st4NNN fields report boolean strings — not 'on'/'off'
		if (isShellyPresence(device?.type)) return 'true';
		return 'on';
	};
	/**
	 * Remove all presence-related conditions from alarm_object for any presence device
	 * that is no longer in selectedItems. Clears both prsn_st4NNN zone conditions and
	 * the dev:decimal:presence anchor written by SelectZonesStep when all zones are
	 * unchecked. Called after every update_alarm invocation.
	 */
	const clearOrphanedPresenceZones = () => {
		const selectedDecimals = new Set<string>(
			selectedItems
				.filter((item) => isShellyPresence($devices[item.id]?.type))
				.map((item) => String(parseInt(item.id, 16))),
		);

		/**
		 * Returns true for any alarm condition that belongs to a presence device
		 * no longer selected — covers both zone (prsn_st4NNN) and anchor (presence) fields.
		 */
		const isOrphanedZone = (el: any) => {
			const parts: string[] = el?.eq?.[0]?.rr?.split(':') ?? [];
			if (parts[0] !== 'dev') return false;
			const field = parts[2] ?? '';
			return (
				!selectedDecimals.has(parts[1]) && (/^prsn_st4\d+$/.test(field) || field === 'presence')
			);
		};

		let changed = false;

		if (alarm_object?.if?.or) {
			const filtered = alarm_object.if.or.filter((el) => !isOrphanedZone(el));
			if (filtered.length !== alarm_object.if.or.length) {
				alarm_object.if.or = filtered;
				changed = true;
			}
		}

		if (alarm_object?.if?.and) {
			alarm_object.if.and = alarm_object.if.and.map((group) => {
				if (!group.or) return group;
				const filtered = group.or.filter((el) => !isOrphanedZone(el));
				return filtered.length !== group.or.length ? { ...group, or: filtered } : group;
			});
			changed = true;
		}

		if (changed) alarm_object = alarm_object;
	};

	const handleSelectItem = (id: string | number) => {
		let isSelected = false;

		if (selectedItems.find((item) => item.id == id) !== undefined) {
			selectedItems = selectedItems.filter((item) => item.id !== id);
		} else {
			isSelected = true;

			selectedItems = [
				...selectedItems,
				{
					id: id,
				},
			];

			/**
			 * Prefetch device settings immediately on selection so zone data is
			 * available without delay when the user navigates to the Zones step.
			 */
			const deviceObj = $devices[id as string];
			if (
				deviceObj &&
				isShellyPresence(deviceObj.type) &&
				!readDeviceSettings(id as string)
			) {
				getSettings(deviceObj);
			}
		}

		let roomIndex = -1;
		let cardIndex = -1;
		roomsFiltered.every((room: any, index) => {
			roomIndex = index;
			cardIndex = room.cards.findIndex((card) => {
				return card.id === id;
			});

			if (cardIndex !== -1) {
				return false;
			}

			return true;
		});

		roomsFiltered[roomIndex].cards[cardIndex].selected = isSelected;
	};

	const update_alarm = (dev_id) => {
		let decimal = dev_id.toLowerCase().startsWith('x') ? dev_id : parseInt(dev_id, 16);
		let device_ind_in_alarm;

		if (alarm_object?.if?.or?.length > 0) {
			device_ind_in_alarm = alarm_object?.if?.or?.findIndex(
				(el) => el?.eq?.[0]?.rr?.split(':')?.[1] == decimal,
			);
		} else if (alarm_object.if.and) {
			let foundedDevicesArrIndex = foundedAlarmDevicesAndIndex(alarm_object.if.and);
			device_ind_in_alarm = alarm_object.if?.and?.[foundedDevicesArrIndex]?.or.findIndex(
				(el) => el?.eq?.[0]?.rr?.split(':')?.[1] == decimal,
			);
		}
		let _alarm_copy: any = _deepClone(alarm_object);

		if (device_ind_in_alarm >= 0) {
			//remove it from alarm,
			if (alarm_object.if.or) {
				alarm_object.if.or = _deepCopyArr(
					_alarm_copy?.if?.or?.filter((el) => el?.eq?.[0]?.rr?.split(':')?.[1] != decimal),
				);
			} else if (alarm_object.if.and) {
				let foundedDevicesArrIndex = foundedAlarmDevicesAndIndex(alarm_object.if.and);
				alarm_object.if.and[foundedDevicesArrIndex].or = alarm_object.if.and[
					foundedDevicesArrIndex
				].or?.filter((el) => el?.eq?.[0]?.rr?.split(':')?.[1] != decimal);
			}
		} else {
			if (alarm_object.if.or) {
				// old implementation. Doesnt trigger alarm_object update
				// alarm_object.if.or.push({
				// 	eq: [{ rr: `dev:${decimal}:${findPropByType(dev_id)}` }, 'on'],
				// });

				alarm_object.if.or = [
					...alarm_object.if.or,
					{
						eq: [
							{ rr: `dev:${decimal}:${findPropByType(dev_id)}` },
							findCompValueByType(dev_id),
						],
					},
				];
			} else if (alarm_object.if.and) {
				//when there are 'and' object, the array on index 1 is always the array of devices
				let foundedDevicesArrIndex = foundedAlarmDevicesAndIndex(alarm_object.if.and);
				if (alarm_object?.if?.and?.[foundedDevicesArrIndex]?.or) {
					alarm_object.if.and[foundedDevicesArrIndex].or = [
						...alarm_object?.if?.and?.[foundedDevicesArrIndex]?.or,
						{
							eq: [
								{ rr: `dev:${decimal}:${findPropByType(dev_id)}` },
								findCompValueByType(dev_id),
							],
						},
					];
				}
			}
		}

		clearOrphanedPresenceZones();
	};
	$: sensors = filterSensors(Object.values($devices));
</script>

<Container noScroll padding="scrollPaddings">
	{#if roomsFiltered.length > 0}
		<SelectList
			missingDevicesAlertHeading={$format('missing_devices_alert_label ', {
				values: { type: $format('type_alarm', { default: 'alarm' }) },
				default: 'You have missing device(s) in this {type}.',
			})}
			title={$format('label_selected', {
				default: 'Selected:',
			})}
			titleHighlight={selectedItems.length}
			list={roomsFiltered}
			bind:allCardsRefs={allSensorsRefs}
			on:click={(e) => {
				handleSelectItem(e.detail.id);
				update_alarm(e.detail.id);
			}}
		/>
	{/if}
</Container>
